import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { StudentRegistrationRequest, RegistrationFormState } from '../../types/auth';
import { AuthApi } from '../../services/authApi';

interface StudentRegistrationProps {
  onSuccess?: (email: string) => void;
}

const StudentRegistration: React.FC<StudentRegistrationProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formState, setFormState] = useState<RegistrationFormState>({
    email: { value: '', error: '', touched: false },
    password: { value: '', error: '', touched: false },
    confirmPassword: { value: '', error: '', touched: false },
    firstName: { value: '', error: '', touched: false },
    lastName: { value: '', error: '', touched: false },
    phone: { value: '', error: '', touched: false },
  });

  const [fullName, setFullName] = useState({ value: '', error: '', touched: false });

  // Function to split full name into first and last name
  const splitFullName = (fullNameValue: string): { firstName: string; lastName: string } => {
    const trimmed = fullNameValue.trim();
    const parts = trimmed.split(' ').filter(part => part.length > 0);
    
    if (parts.length === 0) {
      return { firstName: '', lastName: '' };
    } else if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    } else {
      // First word as firstName, rest as lastName
      const firstName = parts[0];
      const lastName = parts.slice(1).join(' ');
      return { firstName, lastName };
    }
  };

  const updateField = (field: keyof RegistrationFormState, value: string, touched: boolean = true) => {
    setFormState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        touched,
        error: touched ? validateField(field, value) : prev[field].error
      }
    }));
  };

  const validateFullName = (value: string): string => {
    if (!value.trim()) return 'Họ tên là bắt buộc';
    if (value.trim().length < 2) return 'Họ tên phải có ít nhất 2 ký tự';
    if (value.trim().length > 100) return 'Họ tên quá dài';
    return '';
  };

  const validateField = (field: keyof RegistrationFormState, value: string): string => {
    switch (field) {
      case 'email':
        if (!value.trim()) return 'Email là bắt buộc';
        if (!AuthApi.validateEmail(value)) return 'Email không hợp lệ';
        return '';
        
      case 'password':
        const passwordValidation = AuthApi.validatePasswordStrength(value);
        return passwordValidation.isValid ? '' : passwordValidation.errors[0];
        
      case 'confirmPassword':
        if (!value) return 'Xác nhận mật khẩu là bắt buộc';
        if (value !== formState.password.value) return 'Mật khẩu xác nhận không khớp';
        return '';
        
      case 'firstName':
        // These are now auto-populated from fullName, so always valid
        return '';
        
      case 'lastName':
        // These are now auto-populated from fullName, so always valid  
        return '';
        
      case 'phone':
        if (!value.trim()) return 'Số điện thoại là bắt buộc';
        if (!AuthApi.validatePhone(value)) return 'Số điện thoại phải có 10 số và bắt đầu bằng số 0';
        return '';
        
      default:
        return '';
    }
  };

  const validateAllFields = (): boolean => {
    let isValid = true;
    const newFormState = { ...formState };

    // Validate full name first
    const fullNameError = validateFullName(fullName.value);
    const newFullName = { ...fullName, error: fullNameError, touched: true };
    setFullName(newFullName);
    
    if (fullNameError) {
      isValid = false;
    } else {
      // Update firstName and lastName from fullName
      const { firstName, lastName } = splitFullName(fullName.value);
      newFormState.firstName = { value: firstName, error: '', touched: true };
      newFormState.lastName = { value: lastName, error: '', touched: true };
    }

    Object.keys(formState).forEach(key => {
      const field = key as keyof RegistrationFormState;
      // Skip firstName and lastName as they're handled above
      if (field === 'firstName' || field === 'lastName') return;
      
      const error = validateField(field, formState[field].value);
      newFormState[field] = {
        ...newFormState[field],
        error,
        touched: true
      };
      if (error) isValid = false;
    });

    setFormState(newFormState);
    return isValid;
  };

  const handleBlur = (field: keyof RegistrationFormState) => {
    updateField(field, formState[field].value, true);
  };

  const getPasswordStrengthColor = (password: string): string => {
    const validation = AuthApi.validatePasswordStrength(password);
    if (!password) return 'text-m2-outline';
    if (validation.isValid) return 'text-m2-secondary';
    if (validation.errors.length <= 2) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPasswordStrengthText = (password: string): string => {
    if (!password) return 'Chưa nhập mật khẩu';
    const validation = AuthApi.validatePasswordStrength(password);
    if (validation.isValid) return 'Mật khẩu mạnh';
    if (validation.errors.length <= 2) return 'Mật khẩu trung bình';
    return 'Mật khẩu yếu';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validateAllFields()) {
      return;
    }

    setIsLoading(true);

    try {
      // Split full name for registration
      const { firstName, lastName } = splitFullName(fullName.value);
      
      const registrationData: StudentRegistrationRequest = {
        email: formState.email.value.trim(),
        password: formState.password.value,
        confirmPassword: formState.confirmPassword.value,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: formState.phone.value.trim(),
      };

      const response = await AuthApi.register(registrationData);

      if (response.success) {
        setIsSuccess(true);
        if (onSuccess) {
          onSuccess(registrationData.email);
        } else {
          // Default success behavior - redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login', { state: { message: 'Đăng ký thành công! Vui lòng đăng nhập.' } });
          }, 3000);
        }
      }

    } catch (error) {
      console.error('Registration failed:', error);
      setGeneralError(error instanceof Error ? error.message : 'Đăng ký thất bại');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-m2-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-m2-surface-container rounded-lg shadow-lg border border-m2-outline-variant p-8 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-m2-on-surface mb-4">Đăng ký thành công!</h2>
          <p className="text-m2-on-surface-variant mb-6">
            Tài khoản của bạn đã được tạo thành công. Bạn có thể đăng nhập và bắt đầu khám phá các khóa học.
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full bg-eh-forest-ink text-eh-cream py-2.5 px-4 rounded-eh-sm hover:bg-eh-forest-shadow transition-colors"
            >
              Đăng nhập ngay
            </Link>
            <Link
              to="/courses"
              className="block w-full bg-m2-surface-high text-m2-on-surface py-2 px-4 rounded-md hover:bg-m2-surface-highest transition-colors"
            >
              Xem khóa học
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-eh-keylime/40 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-eh-cream rounded-eh shadow-eh-soft border border-eh-border p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-m2-on-surface">Đăng ký tài khoản</h2>
          <p className="mt-2 text-m2-on-surface-variant">Tạo tài khoản để tham gia khóa học</p>
        </div>

        {generalError && (
          <div className="mb-6 p-4 bg-m2-error/10 border border-m2-error/30 rounded-md">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-m2-error">{generalError}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-m2-on-surface">
              Họ và tên <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <input
                id="fullName"
                type="text"
                value={fullName.value}
                onChange={(e) => setFullName(prev => ({ ...prev, value: e.target.value, touched: false }))}
                onBlur={() => setFullName(prev => ({ ...prev, error: validateFullName(prev.value), touched: true }))}
                className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                  fullName.error && fullName.touched
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-m2-outline focus:border-m2-primary focus:ring-m2-primary'
                } rounded-md placeholder-m2-outline focus:outline-none focus:ring-1 sm:text-sm`}
                placeholder="Nhập họ và tên đầy đủ"
              />
              <UserIcon className="h-5 w-5 text-m2-outline absolute left-3 top-2.5" />
            </div>
            {fullName.error && fullName.touched && (
              <p className="mt-1 text-sm text-red-600">{fullName.error}</p>
            )}
            {fullName.value && !fullName.error && (
              <p className="mt-1 text-xs text-m2-on-surface-variant">
                Tên: {splitFullName(fullName.value).firstName || '(Chưa có)'} | 
                Họ: {splitFullName(fullName.value).lastName || '(Chưa có)'}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-m2-on-surface">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <input
                id="email"
                type="email"
                value={formState.email.value}
                onChange={(e) => updateField('email', e.target.value, false)}
                onBlur={() => handleBlur('email')}
                className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                  formState.email.error && formState.email.touched
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-m2-outline focus:border-m2-primary focus:ring-m2-primary'
                } rounded-md placeholder-m2-outline focus:outline-none focus:ring-1 sm:text-sm`}
                placeholder="example@email.com"
              />
              <EnvelopeIcon className="h-5 w-5 text-m2-outline absolute left-3 top-2.5" />
            </div>
            {formState.email.error && formState.email.touched && (
              <p className="mt-1 text-sm text-red-600">{formState.email.error}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-m2-on-surface">
              Số điện thoại <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <input
                id="phone"
                type="tel"
                value={formState.phone.value}
                onChange={(e) => updateField('phone', e.target.value, false)}
                onBlur={() => handleBlur('phone')}
                className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                  formState.phone.error && formState.phone.touched
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-m2-outline focus:border-m2-primary focus:ring-m2-primary'
                } rounded-md placeholder-m2-outline focus:outline-none focus:ring-1 sm:text-sm`}
                placeholder="0123456789"
              />
              <PhoneIcon className="h-5 w-5 text-m2-outline absolute left-3 top-2.5" />
            </div>
            {formState.phone.error && formState.phone.touched && (
              <p className="mt-1 text-sm text-red-600">{formState.phone.error}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-m2-on-surface">
              Mật khẩu <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formState.password.value}
                onChange={(e) => updateField('password', e.target.value, false)}
                onBlur={() => handleBlur('password')}
                className={`appearance-none block w-full pl-10 pr-10 py-2 border ${
                  formState.password.error && formState.password.touched
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-m2-outline focus:border-m2-primary focus:ring-m2-primary'
                } rounded-md placeholder-m2-outline focus:outline-none focus:ring-1 sm:text-sm`}
                placeholder="Nhập mật khẩu"
              />
              <LockClosedIcon className="h-5 w-5 text-m2-outline absolute left-3 top-2.5" />
              <button
                type="button"
                className="absolute right-3 top-2.5 text-m2-outline hover:text-m2-on-surface-variant"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formState.password.value && (
              <div className="mt-2">
                <div className={`text-sm ${getPasswordStrengthColor(formState.password.value)}`}>
                  {getPasswordStrengthText(formState.password.value)}
                </div>
              </div>
            )}
            
            {formState.password.error && formState.password.touched && (
              <p className="mt-1 text-sm text-red-600">{formState.password.error}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-m2-on-surface">
              Xác nhận mật khẩu <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formState.confirmPassword.value}
                onChange={(e) => updateField('confirmPassword', e.target.value, false)}
                onBlur={() => handleBlur('confirmPassword')}
                className={`appearance-none block w-full pl-10 pr-10 py-2 border ${
                  formState.confirmPassword.error && formState.confirmPassword.touched
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-m2-outline focus:border-m2-primary focus:ring-m2-primary'
                } rounded-md placeholder-m2-outline focus:outline-none focus:ring-1 sm:text-sm`}
                placeholder="Nhập lại mật khẩu"
              />
              <LockClosedIcon className="h-5 w-5 text-m2-outline absolute left-3 top-2.5" />
              <button
                type="button"
                className="absolute right-3 top-2.5 text-m2-outline hover:text-m2-on-surface-variant"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {formState.confirmPassword.error && formState.confirmPassword.touched && (
              <p className="mt-1 text-sm text-red-600">{formState.confirmPassword.error}</p>
            )}
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-eh-sm text-eh-cream ${
                isLoading
                  ? 'bg-m2-outline cursor-not-allowed'
                  : 'bg-eh-forest-ink hover:bg-eh-forest-shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eh-forest-ink'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Đang xử lý...
                </div>
              ) : (
                'Đăng ký tài khoản'
              )}
            </button>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-m2-on-surface-variant">
              Bạn đã có tài khoản?{' '}
              <Link
                to="/login"
                className="font-medium text-m2-primary hover:text-m2-primary focus:outline-none focus:underline transition ease-in-out duration-150"
              >
                Đăng nhập
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentRegistration;